"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SettingsCodec = exports.SettingsModel = exports.SettingsMappingError = exports.SETTINGS_REVISION_COLUMN = exports.SETTINGS_GENERATION_COLUMN = exports.SETTINGS_GEN_COLUMN = exports.SETTINGS_SLOT_COLUMN = exports.SETTINGS_KEY_COLUMN = exports.SETTINGS_ORDER_COLUMN = exports.SETTINGS_PARENT_COLUMN = void 0;
exports.settingsRowId = settingsRowId;
exports.settingsNaturalKey = settingsNaturalKey;
const zod_1 = require("zod");
const zod_transformed_schema_1 = require("./zod-transformed-schema");
exports.SETTINGS_PARENT_COLUMN = ':parent';
exports.SETTINGS_ORDER_COLUMN = ':order';
exports.SETTINGS_KEY_COLUMN = ':key';
exports.SETTINGS_SLOT_COLUMN = ':slot';
exports.SETTINGS_GEN_COLUMN = ':gen';
exports.SETTINGS_GENERATION_COLUMN = ':generation';
exports.SETTINGS_REVISION_COLUMN = ':revision';
class SettingsMappingError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'SettingsMappingError';
    }
}
exports.SettingsMappingError = SettingsMappingError;
function unwrap(schema) {
    let optional = false;
    let nullable = false;
    const lazies = [];
    let t = schema;
    for (;;) {
        if (t instanceof zod_1.z.ZodOptional) {
            optional = true;
            t = t.unwrap();
        }
        else if (t instanceof zod_1.z.ZodNullable) {
            nullable = true;
            t = t.unwrap();
        }
        else if (t instanceof zod_1.z.ZodDefault ||
            t instanceof zod_1.z.ZodPrefault ||
            t instanceof zod_1.z.ZodReadonly) {
            t = t.unwrap();
        }
        else if (t instanceof zod_1.z.ZodLazy) {
            lazies.push(t);
            t = t.unwrap();
        }
        else if (t instanceof zod_1.z.ZodPipe) {
            t = (0, zod_transformed_schema_1.transformedSchema)(t) ?? t.out;
        }
        else {
            return { schema: t, optional, nullable, lazies };
        }
    }
}
function leafOf(schema) {
    if (schema instanceof zod_1.z.ZodString)
        return { kind: 'string' };
    if (schema instanceof zod_1.z.ZodNumber)
        return { kind: 'number' };
    if (schema instanceof zod_1.z.ZodBoolean)
        return { kind: 'boolean' };
    if (schema instanceof zod_1.z.ZodEnum) {
        return { kind: 'enum', values: schema.options };
    }
    if (schema instanceof zod_1.z.ZodLiteral && schema.values.size === 1) {
        const [value] = schema.values;
        if (typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean') {
            return { kind: 'literal', value };
        }
    }
    return null;
}
/** A union whose options are all literals or enums of strings. */
function literalUnionValues(schema) {
    if (!(schema instanceof zod_1.z.ZodUnion))
        return null;
    const values = [];
    for (const option of schema.options) {
        const leaf = leafOf(unwrap(option).schema);
        if (leaf?.kind === 'enum')
            values.push(...leaf.values);
        else if (leaf?.kind === 'literal' && typeof leaf.value === 'string')
            values.push(leaf.value);
        else
            return null;
    }
    return values;
}
function isComposite(schema) {
    return (schema instanceof zod_1.z.ZodObject ||
        schema instanceof zod_1.z.ZodDiscriminatedUnion ||
        schema instanceof zod_1.z.ZodUnion);
}
function join(base, segment) {
    return base === '' ? segment : `${base}.${segment}`;
}
function variantValue(option, discriminator) {
    const shape = unwrap(option).schema.shape;
    const leaf = leafOf(unwrap(shape[discriminator]).schema);
    if (leaf?.kind !== 'literal') {
        throw new SettingsMappingError(`Discriminator ${discriminator} is not a literal`);
    }
    return String(leaf.value);
}
/** 64-bit FNV-1a as 16 hex digits: stable short row ids. */
function fnv1a64(input) {
    let hash = 0xcbf29ce484222325n;
    const prime = 0x100000001b3n;
    const bytes = new TextEncoder().encode(input);
    for (const byte of bytes) {
        hash ^= BigInt(byte);
        hash = (hash * prime) & 0xffffffffffffffffn;
    }
    return hash.toString(16).padStart(16, '0');
}
function settingsRowId(generation, parentRowId, tableKey, slot, key) {
    return `${tableKey}-${fnv1a64(`${generation}\u0000${parentRowId}\u0000${slot}\u0000${key}`)}`;
}
/** Shared identity for encoding rows and merging concurrent settings edits. */
function settingsNaturalKey(item, keyField) {
    const fields = typeof keyField === 'string' ? [keyField] : keyField;
    const values = fields.map(field => getPath(item, field));
    if (!values.length ||
        values.some(value => typeof value !== 'string' && typeof value !== 'number'))
        return null;
    return typeof keyField === 'string'
        ? String(values[0])
        : JSON.stringify(values);
}
/**
 * The tables of a settings schema: walks the schema once, assigns every
 * collection of objects to its catalog table and derives the columns.
 */
class SettingsModel {
    constructor(rootSchema, catalog) {
        this.rootSchema = rootSchema;
        this.catalog = catalog;
        this.tableByPath = new Map();
        this.columnsByTable = new Map();
        this.parentByTable = new Map();
        for (const def of catalog) {
            this.columnsByTable.set(def.key, new Map());
            for (const path of def.paths) {
                if (this.tableByPath.has(path)) {
                    throw new SettingsMappingError(`Two tables claim ${path}`);
                }
                this.tableByPath.set(path, def);
            }
        }
        const root = this.tableByPath.get('');
        if (!root)
            throw new SettingsMappingError('Catalog has no root table');
        this.parentByTable.set(root.key, null);
        this.addColumn(root, {
            id: exports.SETTINGS_GENERATION_COLUMN,
            type: 'rich-text',
            encoding: 'generation',
        });
        this.addColumn(root, {
            id: exports.SETTINGS_REVISION_COLUMN,
            type: 'number',
            encoding: 'revision',
        });
        this.deriveRow(root, rootSchema, '', '', new Set());
        const unused = catalog.filter(def => !this.parentByTable.has(def.key));
        if (unused.length > 0) {
            throw new SettingsMappingError(`Catalog tables match no schema path: ${unused.map(d => d.key).join(', ')}`);
        }
        this.tables = new Map(catalog.map(def => [
            def.key,
            {
                def,
                parent: this.parentByTable.get(def.key) ?? null,
                columns: [...(this.columnsByTable.get(def.key)?.values() ?? [])],
            },
        ]));
    }
    table(key) {
        const table = this.tables.get(key);
        if (!table)
            throw new SettingsMappingError(`Unknown settings table ${key}`);
        return table;
    }
    tableAt(path) {
        return this.tableByPath.get(path);
    }
    addColumn(table, column) {
        const columns = this.columnsByTable.get(table.key);
        const existing = columns.get(column.id);
        if (!existing) {
            columns.set(column.id, column);
            return;
        }
        const sameKind = existing.type === column.type &&
            existing.encoding === column.encoding &&
            existing.target === column.target;
        if (!sameKind) {
            throw new SettingsMappingError(`Column ${column.id} of ${table.key} has two kinds`);
        }
        if (column.options) {
            const options = [
                ...new Set([...(existing.options ?? []), ...column.options]),
            ];
            columns.set(column.id, { ...existing, options });
        }
    }
    isJson(table, rel) {
        return table.json?.includes(rel) ?? false;
    }
    isSkipped(table, rel) {
        return table.skip?.includes(rel) ?? false;
    }
    /** Columns of one table row; `abs` is the schema path, `rel` the column path. */
    deriveRow(table, schema, abs, rel, stack) {
        if (rel !== '' && this.isSkipped(table, rel))
            return;
        const { schema: t, optional, nullable, lazies } = unwrap(schema);
        if (rel !== '' && this.isJson(table, rel)) {
            this.addColumn(table, { id: rel, type: 'rich-text', encoding: 'json' });
            return;
        }
        if (stack.has(t) || lazies.some(lazy => stack.has(lazy))) {
            throw new SettingsMappingError(`Recursive schema at ${abs} needs a json entry in ${table.key}`);
        }
        if (rel !== '' && optional && nullable) {
            this.addColumn(table, {
                id: `${rel}:null`,
                type: 'checkbox',
                encoding: 'null',
            });
        }
        const leaf = leafOf(t);
        if (leaf) {
            if (rel === '') {
                throw new SettingsMappingError(`Row of ${table.key} is a scalar`);
            }
            if (leaf.kind === 'literal') {
                if (optional || nullable) {
                    this.addColumn(table, {
                        id: rel,
                        type: 'checkbox',
                        encoding: 'presence',
                    });
                }
                return;
            }
            this.addColumn(table, {
                id: rel,
                type: leaf.kind === 'enum'
                    ? 'select'
                    : leaf.kind === 'number'
                        ? 'number'
                        : leaf.kind === 'boolean'
                            ? 'checkbox'
                            : 'rich-text',
                encoding: 'scalar',
                ...(leaf.kind === 'enum' ? { options: leaf.values } : {}),
            });
            // A boolean that may be absent needs a presence column too.
            if (leaf.kind === 'boolean' && (optional || nullable)) {
                this.addColumn(table, {
                    id: `${rel}:set`,
                    type: 'checkbox',
                    encoding: 'presence',
                });
            }
            return;
        }
        const unionValues = literalUnionValues(t);
        if (unionValues) {
            this.addColumn(table, {
                id: rel,
                type: 'select',
                encoding: 'scalar',
                options: unionValues,
            });
            return;
        }
        stack.add(t);
        for (const lazy of lazies)
            stack.add(lazy);
        try {
            if (t instanceof zod_1.z.ZodObject) {
                const catchall = t._zod.def.catchall;
                if (catchall && !(catchall instanceof zod_1.z.ZodNever)) {
                    throw new SettingsMappingError(`Passthrough object at ${abs}`);
                }
                if (rel !== '' && (optional || nullable)) {
                    this.addColumn(table, {
                        id: rel,
                        type: 'checkbox',
                        encoding: 'presence',
                    });
                }
                for (const [field, fieldSchema] of Object.entries(t.shape)) {
                    this.deriveRow(table, fieldSchema, join(abs, field), join(rel, field), stack);
                }
                return;
            }
            if (t instanceof zod_1.z.ZodDiscriminatedUnion) {
                const discriminator = t._zod.def.discriminator;
                const options = t.options.map(o => variantValue(o, discriminator));
                if (rel !== '' && (optional || nullable)) {
                    this.addColumn(table, {
                        id: rel,
                        type: 'checkbox',
                        encoding: 'presence',
                    });
                }
                this.addColumn(table, {
                    id: join(rel, discriminator),
                    type: 'select',
                    encoding: 'scalar',
                    options,
                });
                for (const option of t.options) {
                    const variant = variantValue(option, discriminator);
                    const shape = unwrap(option).schema.shape;
                    for (const [field, fieldSchema] of Object.entries(shape)) {
                        if (field === discriminator)
                            continue;
                        this.deriveRow(table, fieldSchema, join(`${abs}|${variant}`, field), join(rel, field), stack);
                    }
                }
                return;
            }
            if (t instanceof zod_1.z.ZodArray || t instanceof zod_1.z.ZodRecord) {
                const isRecord = t instanceof zod_1.z.ZodRecord;
                const element = unwrap(isRecord ? t.valueType : t.element);
                const childAbs = `${abs}${isRecord ? '{}' : '[]'}`;
                const childTable = this.tableByPath.get(childAbs);
                if (childTable) {
                    this.attachTable(childTable, table, element.schema, childAbs, stack);
                    if (optional || nullable) {
                        this.addColumn(table, {
                            id: rel,
                            type: 'checkbox',
                            encoding: 'presence',
                        });
                    }
                    return;
                }
                if (this.isJson(table, `${rel}{}`) || this.isJson(table, `${rel}[]`)) {
                    this.addColumn(table, {
                        id: rel,
                        type: 'rich-text',
                        encoding: 'json',
                    });
                    return;
                }
                const elementLeaf = leafOf(element.schema);
                if (!elementLeaf || element.optional || element.nullable) {
                    throw new SettingsMappingError(`Collection ${childAbs} needs a table or a json entry in ${table.key}`);
                }
                if (optional || nullable) {
                    this.addColumn(table, {
                        id: `${rel}:set`,
                        type: 'checkbox',
                        encoding: 'presence',
                    });
                }
                if (isRecord) {
                    if (elementLeaf.kind !== 'string' && elementLeaf.kind !== 'enum') {
                        throw new SettingsMappingError(`Map ${childAbs} of non-text values`);
                    }
                    this.addColumn(table, {
                        id: rel,
                        type: 'rich-text',
                        encoding: 'entries',
                    });
                    return;
                }
                if (elementLeaf.kind === 'enum') {
                    this.addColumn(table, {
                        id: rel,
                        type: 'multi-select',
                        encoding: 'options',
                        options: elementLeaf.values,
                    });
                    return;
                }
                if (elementLeaf.kind === 'string' || elementLeaf.kind === 'number') {
                    this.addColumn(table, {
                        id: rel,
                        type: 'rich-text',
                        encoding: 'lines',
                    });
                    return;
                }
                throw new SettingsMappingError(`List ${childAbs} of ${elementLeaf.kind}`);
            }
            throw new SettingsMappingError(`Unsupported schema ${t._zod.def.type} at ${abs} needs a json entry in ${table.key}`);
        }
        finally {
            stack.delete(t);
            for (const lazy of lazies)
                stack.delete(lazy);
        }
    }
    attachTable(child, parent, element, abs, stack) {
        const known = this.parentByTable.get(child.key);
        if (known !== undefined && known !== parent.key) {
            throw new SettingsMappingError(`Table ${child.key} has two parent tables (${known}, ${parent.key})`);
        }
        this.parentByTable.set(child.key, parent.key);
        this.addColumn(child, {
            id: exports.SETTINGS_PARENT_COLUMN,
            type: 'relation',
            encoding: 'parent',
            target: parent.key,
        });
        this.addColumn(child, {
            id: exports.SETTINGS_SLOT_COLUMN,
            type: 'rich-text',
            encoding: 'slot',
        });
        this.addColumn(child, {
            id: exports.SETTINGS_KEY_COLUMN,
            type: 'rich-text',
            encoding: 'key',
        });
        this.addColumn(child, {
            id: exports.SETTINGS_ORDER_COLUMN,
            type: 'number',
            encoding: 'order',
        });
        this.addColumn(child, {
            id: exports.SETTINGS_GEN_COLUMN,
            type: 'rich-text',
            encoding: 'gen',
        });
        if (!isComposite(unwrap(element).schema) &&
            !leafOf(unwrap(element).schema)) {
            throw new SettingsMappingError(`Table ${child.key} rows are not objects`);
        }
        const leaf = leafOf(unwrap(element).schema);
        if (leaf) {
            this.deriveRow(child, element, abs, 'value', stack);
            return;
        }
        this.deriveRow(child, element, abs, '', stack);
    }
}
exports.SettingsModel = SettingsModel;
function getPath(value, path) {
    let current = value;
    for (const segment of path.split('.')) {
        if (!current || typeof current !== 'object')
            return undefined;
        current = current[segment];
    }
    return current;
}
function assertLine(value, where) {
    if (value === '' || value.includes('\n')) {
        throw new SettingsMappingError(`${where}: list entries must be non-empty single lines`);
    }
}
function childKey(parentId, table, slot) {
    return `${parentId}\u0000${table}\u0000${slot}`;
}
/** Encodes and decodes one settings object with a {@link SettingsModel}. */
class SettingsCodec {
    constructor(model) {
        this.model = model;
    }
    /** Rows of one generation; the root row names it as the valid one. */
    encode(rootRowId, value, state) {
        if (state.generation === '' || state.generation.includes('\u0000')) {
            throw new SettingsMappingError('Generation needs a non-empty write id');
        }
        const root = this.model.tableAt('');
        const ctx = {
            rows: new Map(),
            ids: new Set(),
            generation: state.generation,
        };
        const cells = {
            [exports.SETTINGS_GENERATION_COLUMN]: state.generation,
            [exports.SETTINGS_REVISION_COLUMN]: state.revision,
        };
        this.encodeValue(root, this.model.rootSchema, value, '', '', rootRowId, cells, ctx);
        this.pushRow(ctx, root, {
            id: rootRowId,
            title: this.titleOf(root, value, rootRowId),
            cells,
        });
        return Object.fromEntries([...this.model.tables.keys()].map(key => [key, ctx.rows.get(key) ?? []]));
    }
    /** Generation and revision the root row names. */
    state(rootRowId, rows) {
        const root = rows[this.model.tableAt('').key]?.find(row => row.id === rootRowId);
        if (!root)
            throw new SettingsMappingError(`Container row ${rootRowId} is missing`);
        const generation = root.cells[exports.SETTINGS_GENERATION_COLUMN];
        const revision = root.cells[exports.SETTINGS_REVISION_COLUMN];
        if (typeof generation !== 'string' || generation === '') {
            throw new SettingsMappingError(`Container row ${rootRowId} names no generation`);
        }
        if (typeof revision !== 'number') {
            throw new SettingsMappingError(`Container row ${rootRowId} has no revision`);
        }
        return { generation, revision };
    }
    /** The object of the valid generation; rows of other generations are ignored. */
    decode(rootRowId, rows) {
        const { generation } = this.state(rootRowId, rows);
        const byId = new Map();
        const children = new Map();
        for (const [tableKey, tableRows] of Object.entries(rows)) {
            const table = this.model.table(tableKey);
            for (const row of tableRows) {
                if (table.parent !== null &&
                    row.cells[exports.SETTINGS_GEN_COLUMN] !== generation) {
                    continue;
                }
                if (byId.has(row.id)) {
                    throw new SettingsMappingError(`Row ${row.id} appears twice`);
                }
                byId.set(row.id, row);
                if (table.parent === null)
                    continue;
                const parent = row.cells[exports.SETTINGS_PARENT_COLUMN];
                const slot = row.cells[exports.SETTINGS_SLOT_COLUMN];
                if (!Array.isArray(parent) ||
                    parent.length !== 1 ||
                    typeof slot !== 'string') {
                    throw new SettingsMappingError(`Row ${row.id} of ${tableKey} has no single parent`);
                }
                const key = childKey(parent[0], tableKey, slot);
                const list = children.get(key) ?? [];
                list.push(row);
                children.set(key, list);
            }
        }
        for (const list of children.values()) {
            list.sort((a, b) => Number(a.cells[exports.SETTINGS_ORDER_COLUMN]) -
                Number(b.cells[exports.SETTINGS_ORDER_COLUMN]));
        }
        const root = byId.get(rootRowId);
        if (!root)
            throw new SettingsMappingError(`Container row ${rootRowId} is missing`);
        const index = { byId, children };
        return this.decodeValue(this.model.tableAt(''), this.model.rootSchema, root, '', '', index);
    }
    titleOf(table, value, fallback) {
        const title = table.titleField
            ? getPath(value, table.titleField)
            : undefined;
        return typeof title === 'string' && title !== '' ? title : fallback;
    }
    pushRow(ctx, table, row) {
        if (ctx.ids.has(row.id)) {
            throw new SettingsMappingError(`Row id ${row.id} of ${table.key} is not unique`);
        }
        ctx.ids.add(row.id);
        const list = ctx.rows.get(table.key) ?? [];
        list.push(row);
        ctx.rows.set(table.key, list);
    }
    isJson(table, rel) {
        return table.json?.includes(rel) ?? false;
    }
    encodeValue(table, schema, value, abs, rel, rowId, cells, ctx) {
        if (rel !== '' && table.skip?.includes(rel))
            return;
        if (rel !== '' && this.isJson(table, rel)) {
            if (value !== undefined)
                cells[rel] = JSON.stringify(value);
            return;
        }
        const { schema: t, optional, nullable } = unwrap(schema);
        if (value === undefined)
            return;
        if (value === null) {
            if (optional && nullable)
                cells[`${rel}:null`] = true;
            return;
        }
        const leaf = leafOf(t);
        if (leaf) {
            if (leaf.kind === 'literal') {
                if (optional || nullable)
                    cells[rel] = true;
                return;
            }
            if (leaf.kind === 'boolean' && (optional || nullable))
                cells[`${rel}:set`] = true;
            cells[rel] = value;
            return;
        }
        if (literalUnionValues(t)) {
            cells[rel] = value;
            return;
        }
        if (t instanceof zod_1.z.ZodObject) {
            if (rel !== '' && (optional || nullable))
                cells[rel] = true;
            const record = value;
            for (const [field, fieldSchema] of Object.entries(t.shape)) {
                this.encodeValue(table, fieldSchema, record[field], join(abs, field), join(rel, field), rowId, cells, ctx);
            }
            return;
        }
        if (t instanceof zod_1.z.ZodDiscriminatedUnion) {
            if (rel !== '' && (optional || nullable))
                cells[rel] = true;
            const discriminator = t._zod.def.discriminator;
            const record = value;
            const variant = String(record[discriminator]);
            const option = t.options.find(o => variantValue(o, discriminator) === variant);
            if (!option) {
                throw new SettingsMappingError(`Unknown ${discriminator} ${variant} at ${abs}`);
            }
            cells[join(rel, discriminator)] = variant;
            const shape = unwrap(option).schema.shape;
            for (const [field, fieldSchema] of Object.entries(shape)) {
                if (field === discriminator)
                    continue;
                this.encodeValue(table, fieldSchema, record[field], join(`${abs}|${variant}`, field), join(rel, field), rowId, cells, ctx);
            }
            return;
        }
        if (t instanceof zod_1.z.ZodArray || t instanceof zod_1.z.ZodRecord) {
            const isRecord = t instanceof zod_1.z.ZodRecord;
            const element = isRecord ? t.valueType : t.element;
            const childAbs = `${abs}${isRecord ? '{}' : '[]'}`;
            const child = this.model.tableAt(childAbs);
            const entries = isRecord
                ? Object.entries(value)
                : value.map((item, index) => [String(index), item]);
            if (child) {
                if (optional || nullable)
                    cells[rel] = true;
                const elementLeaf = leafOf(unwrap(element).schema);
                entries.forEach(([entryKey, item], order) => {
                    const natural = !isRecord && child.keyField
                        ? settingsNaturalKey(item, child.keyField)
                        : undefined;
                    const key = typeof natural === 'string' || typeof natural === 'number'
                        ? String(natural)
                        : entryKey;
                    const childId = settingsRowId(ctx.generation, rowId, child.key, rel, key);
                    const childCells = {
                        [exports.SETTINGS_PARENT_COLUMN]: [rowId],
                        [exports.SETTINGS_SLOT_COLUMN]: rel,
                        [exports.SETTINGS_KEY_COLUMN]: entryKey,
                        [exports.SETTINGS_ORDER_COLUMN]: order,
                        [exports.SETTINGS_GEN_COLUMN]: ctx.generation,
                    };
                    this.encodeValue(child, element, item, childAbs, elementLeaf ? 'value' : '', childId, childCells, ctx);
                    this.pushRow(ctx, child, {
                        id: childId,
                        title: this.titleOf(child, item, key),
                        cells: childCells,
                    });
                });
                return;
            }
            if (this.isJson(table, `${rel}{}`) || this.isJson(table, `${rel}[]`)) {
                cells[rel] = JSON.stringify(value);
                return;
            }
            const elementLeaf = leafOf(unwrap(element).schema);
            if (optional || nullable)
                cells[`${rel}:set`] = true;
            if (isRecord) {
                cells[rel] = entries
                    .map(([key, item]) => {
                    const text = String(item);
                    if (key.includes(': ') || key === '') {
                        throw new SettingsMappingError(`${abs}: map key "${key}" cannot be a line key`);
                    }
                    const line = `${key}: ${text}`;
                    assertLine(line, abs);
                    return line;
                })
                    .join('\n');
                return;
            }
            if (elementLeaf.kind === 'enum') {
                cells[rel] = entries.map(([, item]) => String(item));
                return;
            }
            cells[rel] = entries
                .map(([, item]) => {
                const text = String(item);
                assertLine(text, abs);
                return text;
            })
                .join('\n');
            return;
        }
        throw new SettingsMappingError(`Cannot encode ${t._zod.def.type} at ${abs}`);
    }
    decodeValue(table, schema, row, abs, rel, index) {
        if (rel !== '' && table.skip?.includes(rel))
            return undefined;
        const cells = row.cells;
        if (rel !== '' && this.isJson(table, rel)) {
            const text = cells[rel];
            return typeof text === 'string' ? JSON.parse(text) : undefined;
        }
        const { schema: t, optional, nullable } = unwrap(schema);
        const absent = () => (nullable && !optional ? null : undefined);
        if (optional && nullable && cells[`${rel}:null`] === true)
            return null;
        const leaf = leafOf(t);
        if (leaf) {
            if (leaf.kind === 'literal') {
                if (!optional && !nullable)
                    return leaf.value;
                return cells[rel] === true ? leaf.value : absent();
            }
            if (leaf.kind === 'boolean' && (optional || nullable)) {
                return cells[`${rel}:set`] === true ? cells[rel] === true : absent();
            }
            const cell = cells[rel];
            if (cell === undefined)
                return absent();
            if (leaf.kind === 'boolean')
                return cell === true;
            return cell;
        }
        if (literalUnionValues(t)) {
            const cell = cells[rel];
            return cell === undefined ? absent() : cell;
        }
        if (t instanceof zod_1.z.ZodObject) {
            if (rel !== '' && (optional || nullable) && cells[rel] !== true)
                return absent();
            const out = {};
            for (const [field, fieldSchema] of Object.entries(t.shape)) {
                const decoded = this.decodeValue(table, fieldSchema, row, join(abs, field), join(rel, field), index);
                if (decoded !== undefined)
                    out[field] = decoded;
            }
            return out;
        }
        if (t instanceof zod_1.z.ZodDiscriminatedUnion) {
            if (rel !== '' && (optional || nullable) && cells[rel] !== true)
                return absent();
            const discriminator = t._zod.def.discriminator;
            const variant = cells[join(rel, discriminator)];
            const option = t.options.find(o => variantValue(o, discriminator) === variant);
            if (!option) {
                throw new SettingsMappingError(`Row ${row.id}: unknown ${discriminator} ${String(variant)}`);
            }
            const shape = unwrap(option).schema.shape;
            const literal = leafOf(unwrap(shape[discriminator]).schema);
            const out = {
                [discriminator]: literal?.kind === 'literal' ? literal.value : variant,
            };
            for (const [field, fieldSchema] of Object.entries(shape)) {
                if (field === discriminator)
                    continue;
                const decoded = this.decodeValue(table, fieldSchema, row, join(`${abs}|${variant}`, field), join(rel, field), index);
                if (decoded !== undefined)
                    out[field] = decoded;
            }
            return out;
        }
        if (t instanceof zod_1.z.ZodArray || t instanceof zod_1.z.ZodRecord) {
            const isRecord = t instanceof zod_1.z.ZodRecord;
            const element = isRecord ? t.valueType : t.element;
            const childAbs = `${abs}${isRecord ? '{}' : '[]'}`;
            const child = this.model.tableAt(childAbs);
            if (child) {
                if ((optional || nullable) && cells[rel] !== true)
                    return absent();
                const elementLeaf = leafOf(unwrap(element).schema);
                const rows = index.children.get(childKey(row.id, child.key, rel)) ?? [];
                const items = rows.map(childRow => [
                    String(childRow.cells[exports.SETTINGS_KEY_COLUMN]),
                    this.decodeValue(child, element, childRow, childAbs, elementLeaf ? 'value' : '', index),
                ]);
                return isRecord
                    ? Object.fromEntries(items)
                    : items.map(([, item]) => item);
            }
            if (this.isJson(table, `${rel}{}`) || this.isJson(table, `${rel}[]`)) {
                const text = cells[rel];
                return typeof text === 'string' ? JSON.parse(text) : absent();
            }
            const elementLeaf = leafOf(unwrap(element).schema);
            if ((optional || nullable) && cells[`${rel}:set`] !== true)
                return absent();
            const cell = cells[rel];
            if (isRecord) {
                const text = typeof cell === 'string' ? cell : '';
                return Object.fromEntries(text === ''
                    ? []
                    : text.split('\n').map(line => {
                        const at = line.indexOf(': ');
                        return [line.slice(0, at), line.slice(at + 2)];
                    }));
            }
            if (elementLeaf.kind === 'enum')
                return Array.isArray(cell) ? [...cell] : [];
            const text = typeof cell === 'string' ? cell : '';
            const lines = text === '' ? [] : text.split('\n');
            return elementLeaf.kind === 'number' ? lines.map(Number) : lines;
        }
        throw new SettingsMappingError(`Cannot decode ${t._zod.def.type} at ${abs}`);
    }
}
exports.SettingsCodec = SettingsCodec;
